import { pageName } from '../shared/pages';

export interface Breadcrumb {
  at: string;
  text: string;
}

export interface Diagnostics {
  pageName: string;
  page: string;
  viewport: string;
  screen: string;
  devicePixelRatio: number;
  userAgent: string;
  timeZone: string;
  locale: string;
  online: boolean;
  build?: string;
  recent: Breadcrumb[];
}

const RECENT_MAX = 15;
const TEXT_MAX = 300;
const RELOAD_KEY = 'ftlo.staleBuildReloadAt';
const RELOAD_GAP_MS = 10_000;

const recent: Breadcrumb[] = [];

export function recordEvent(text: string): void {
  recent.push({ at: new Date().toISOString(), text: text.slice(0, TEXT_MAX) });
  if (recent.length > RECENT_MAX) recent.shift();
}

export function clearEvents(): void {
  recent.length = 0;
}

function describe(reason: unknown): string {
  if (reason instanceof Error) {
    const site = reason.stack?.split('\n')[1]?.trim();
    return site
      ? `${reason.name}: ${reason.message} (${site})`
      : String(reason);
  }
  return String(reason);
}

export function watchForErrors(): void {
  window.addEventListener('error', (event) => {
    recordEvent(
      event.error
        ? describe(event.error)
        : `${event.message} (${event.filename}:${event.lineno})`,
    );
  });
  window.addEventListener('unhandledrejection', (event) => {
    recordEvent(`Unhandled rejection: ${describe(event.reason)}`);
  });
  window.addEventListener('vite:preloadError', (event) => {
    recordEvent(`Stale build: ${describe(event.payload)}`);
    const since = Date.now() - Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (since < RELOAD_GAP_MS) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    event.preventDefault();
    window.location.reload();
  });
}

export function collectDiagnostics(): Diagnostics {
  return {
    pageName: pageName(window.location.pathname),
    page: window.location.pathname + window.location.search,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen.width}x${window.screen.height}`,
    devicePixelRatio: window.devicePixelRatio,
    userAgent: navigator.userAgent,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,
    online: navigator.onLine,
    ...(import.meta.env.VITE_BUILD_ID
      ? { build: import.meta.env.VITE_BUILD_ID }
      : {}),
    recent: [...recent],
  };
}
